import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Radio, X, ExternalLink, GripVertical } from "lucide-react";
import { useLivePlayer } from "@/context/LivePlayerContext";

const VIDEO_ID = "QB5BNdBFujE";
// autoplay + son (l'activation vient d'un clic utilisateur), lecteur avec contrôles
const SRC = `https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&mute=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;
const WATCH = `https://youtu.be/${VIDEO_ID}`;

const MINI_W = 360;
const BAR_H = 30;
const MARGIN = 16;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

function loadPos() {
  try { return JSON.parse(localStorage.getItem("livePlayerPos")) || null; } catch { return null; }
}

// Lecteur monté UNE SEULE FOIS dans le Layout : l'iframe ne se démonte jamais.
// Sur /dashboard il colle à son emplacement (grand) ; ailleurs il devient un
// lecteur flottant déplaçable via sa BARRE DE TITRE (la vidéo dessous reste
// entièrement cliquable). Suivi via requestAnimationFrame (fluide, sans délai).
export function PersistentLivePlayer() {
  const { open, closePlayer } = useLivePlayer();
  const { pathname } = useLocation();
  const [box, setBox] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [animate, setAnimate] = useState(false);
  const boxRef = useRef(null);
  const posRef = useRef(loadPos());
  const dragRef = useRef(null);
  const animTimer = useRef(null);

  const miniSize = useCallback(() => {
    const w = Math.min(MINI_W, window.innerWidth - 2 * MARGIN);
    return { w, h: Math.round((w * 9) / 16) + BAR_H };
  }, []);

  const applyBox = useCallback((next) => {
    const cur = boxRef.current;
    if (cur && cur.top === next.top && cur.left === next.left && cur.width === next.width && cur.height === next.height && cur.big === next.big) return;
    if (cur && cur.big !== next.big) {
      setAnimate(true);
      clearTimeout(animTimer.current);
      animTimer.current = setTimeout(() => setAnimate(false), 320);
    }
    boxRef.current = next;
    setBox(next);
  }, []);

  const measure = useCallback(() => {
    if (dragRef.current) return;
    const anchor = pathname === "/dashboard" ? document.querySelector("[data-live-anchor]") : null;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      const visible = r.height > 40 && r.bottom > 90 && r.top < window.innerHeight - 30;
      if (visible) {
        applyBox({ top: Math.round(r.top), left: Math.round(r.left), width: Math.round(r.width), height: Math.round(r.height), big: true });
        return;
      }
    }
    const { w, h } = miniSize();
    const p = posRef.current;
    const left = Math.round(p ? clamp(p.left, MARGIN, window.innerWidth - w - MARGIN) : window.innerWidth - w - MARGIN);
    const top = Math.round(p ? clamp(p.top, MARGIN, window.innerHeight - h - MARGIN) : window.innerHeight - h - MARGIN);
    applyBox({ top, left, width: w, height: h, big: false });
  }, [pathname, miniSize, applyBox]);

  useEffect(() => {
    if (!open) return;
    let raf;
    const tick = () => { measure(); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, measure]);

  const onDragMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const { w, h } = miniSize();
    const left = Math.round(clamp(d.left + (e.clientX - d.x), MARGIN, window.innerWidth - w - MARGIN));
    const top = Math.round(clamp(d.top + (e.clientY - d.y), MARGIN, window.innerHeight - h - MARGIN));
    posRef.current = { left, top };
    const next = { ...(boxRef.current || {}), left, top, width: w, height: h, big: false };
    boxRef.current = next;
    setBox(next);
  }, [miniSize]);

  const onDragEnd = useCallback(() => {
    dragRef.current = null;
    setDragging(false);
    if (posRef.current) localStorage.setItem("livePlayerPos", JSON.stringify(posRef.current));
    window.removeEventListener("pointermove", onDragMove);
    window.removeEventListener("pointerup", onDragEnd);
    document.body.style.userSelect = "";
  }, [onDragMove]);

  const onDragStart = useCallback((e) => {
    if (!boxRef.current || boxRef.current.big) return;
    e.preventDefault();
    dragRef.current = { x: e.clientX, y: e.clientY, left: boxRef.current.left, top: boxRef.current.top };
    setDragging(true);
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
  }, [onDragMove, onDragEnd]);

  useEffect(() => () => clearTimeout(animTimer.current), []);

  if (!open || !box) return null;
  const big = box.big;

  return (
    <div
      data-testid="persistent-live-player"
      data-live-mode={big ? "big" : "mini"}
      style={{
        top: box.top, left: box.left, width: box.width, height: box.height,
        transition: animate ? "top .3s ease-out, left .3s ease-out, width .3s ease-out, height .3s ease-out" : "none",
        willChange: "top, left, width, height",
      }}
      className={`fixed z-40 flex flex-col overflow-hidden border border-[#222733] bg-black shadow-2xl ${big ? "rounded-lg" : "rounded-xl ring-1 ring-black/40"}`}>
      {/* Barre de titre = poignée de déplacement (mode flottant). N'overlappe PAS la vidéo. */}
      <div
        onPointerDown={big ? undefined : onDragStart}
        data-testid="live-player-drag-handle"
        style={{ height: BAR_H }}
        className={`flex shrink-0 items-center justify-between border-b border-[#222733] bg-[#11141A] px-2 ${big ? "" : "cursor-move select-none"}`}>
        <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/90">
          {!big && <GripVertical className="h-3.5 w-3.5 text-white/40" />}
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
          <Radio className="h-3 w-3 text-amber-400" /> Direct
        </span>
        <span className="flex items-center gap-0.5">
          <a href={WATCH} target="_blank" rel="noopener noreferrer" title="Ouvrir sur YouTube"
            onPointerDown={(e) => e.stopPropagation()}
            className="rounded p-1 text-white/70 hover:text-white"><ExternalLink className="h-3.5 w-3.5" /></a>
          <button onClick={closePlayer} onPointerDown={(e) => e.stopPropagation()} data-testid="live-player-close" title="Fermer"
            className="rounded p-1 text-white/70 hover:text-white"><X className="h-3.5 w-3.5" /></button>
        </span>
      </div>
      {/* Vidéo : entièrement cliquable (son, plein écran, etc.) */}
      <div className="relative flex-1 bg-black">
        <iframe
          className="absolute inset-0 h-full w-full"
          src={SRC}
          title="Direct marché"
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          data-testid="persistent-live-iframe"
        />
        {dragging && <div className="absolute inset-0 z-20 cursor-move" data-testid="live-drag-overlay" />}
      </div>
    </div>
  );
}
